import { Injectable } from '@angular/core';
import { AvanoLoadingIndicatorDirective } from './avano-loading-indicator.directive';

/**
 * This service class offers functionality to block/unblock UI elements
 * at the begin and end of an async data operation.
 * It makes use of the {@link AvanoLoadingIndicatorDirective} to render the UI states.
 *
 * NOTE: Currently it is only possible to block manually, if it should block directly according to an observable
 * write an observable operator? or a base http interceptor
 *
 */
@Injectable({
  providedIn: 'root'
})
export class AvanoLoadingIndicatorService {
  static readonly LOADING_IDENTIFIER_APPLICATION = "app_loading_block";

  /** Object to hold registered {@link AvanoLoadingIndicatorDirective}. */
  private directives: Map<string, AvanoLoadingIndicatorDirective[]> = new Map();
  private toBlockOnRegister: string[] = [];

  /**
   * Register the given AvanoLoadingIndicatorDirective instance using the directive's name attribute
   * as lookup key.
   * This method is intended to be used within the init phase of the AvanoLoadingIndicatorDirective
   * @param {AvanoLoadingIndicatorDirective} loadingDirective - the directive instance to be used for later usage
   */
  register(loadingDirective: AvanoLoadingIndicatorDirective): void {
    if (!this.directives.has(loadingDirective.avanoLoadingIndicator)) {
      this.directives.set(loadingDirective.avanoLoadingIndicator, []);
    }
    this.directives.get(loadingDirective.avanoLoadingIndicator).push(loadingDirective);
    this.checkIfAlreadyBlocking(loadingDirective.avanoLoadingIndicator);
  }

  /**
   * Deregister the given AvanoLoadingIndicatorDirective within the destroy phase of the directive instance
   * @param {AvanoLoadingIndicatorDirective} loadingDirective - the directive instance which is about to be destroyed
   */
  deregister(loadingDirective: AvanoLoadingIndicatorDirective): void {
    const allDirectivesUnderIndicator = this.directives.get(loadingDirective.avanoLoadingIndicator);
    const indexToRemove = allDirectivesUnderIndicator.indexOf(loadingDirective);
    allDirectivesUnderIndicator.splice(indexToRemove, 1);
    if (!this.directives.get(loadingDirective.avanoLoadingIndicator).length) {
      this.directives.delete(loadingDirective.avanoLoadingIndicator);
    }
    this.checkIfAlreadyBlocking(loadingDirective.avanoLoadingIndicator, false);
  }

  blockApplication(): void {
    this.block([AvanoLoadingIndicatorService.LOADING_IDENTIFIER_APPLICATION]);
  }

  unblockApplication(): void {
    this.unblock([AvanoLoadingIndicatorService.LOADING_IDENTIFIER_APPLICATION]);
  }

  /**
   * Block the list of UI elements identified by their element names.
   * @param {String} names - an array of element names - each name should match the name attribute of the AvanoLoadingIndicatorDirective
   * @param {boolean} debounceInMs - skip the styleguide defined debounce time before showing loader. Setting to 'false' reduces flickering for fast requests
   */
  block(names: string[], debounceInMs: number = 300): void {
    if (!(debounceInMs > 1)){ this.doBlock(names); return; }
    
    this.toBlockOnRegister.push(...names);
    setTimeout(() => {
      for (const name of names) {
        if (this.toBlockOnRegister.includes(name)) {
          this.doBlock([name])
        }
      }
    }, debounceInMs);
  }

  /**
   * Unblock the list of UI elements identified by their element names.
   * @param {String} names - an array of element names - each name should match the name attribute of the AvanoLoadingIndicatorDirective
   */
  unblock(names: string[]): void {
    for (const name of names) {
      if (this.directives.has(name)) {
        this.directives.get(name).forEach((singleDirective) => singleDirective.unblock());
      }
      this.checkIfAlreadyBlocking(name, false);
    }
  }

  private doBlock(names: string[]) {
    for (const name of names) {
      if (this.directives.has(name)) {
        this.directives.get(name).forEach((singleDirective) => singleDirective.block());
        this.checkIfAlreadyBlocking(name, false);
      } else {
        // The container is not yet registered, angular will do it in some seconds
        // so add the name to the list of things to be directly blocked when they register themselves
        this.toBlockOnRegister.push(name);
      }
    }
  }

  private checkIfAlreadyBlocking(nameToBlock: string, block: boolean = true): void {
    this.toBlockOnRegister = [...new Set(this.toBlockOnRegister)];
    const indexOfNameToBlock = this.toBlockOnRegister.indexOf(nameToBlock);
    if (indexOfNameToBlock >= 0) {
      this.toBlockOnRegister.splice(indexOfNameToBlock, 1);
      if (block) {
        this.block([nameToBlock]);
      }
    }
  }
}
